import { useRef, useState } from "react";
import { Bell } from "lucide-react";
import { IconButton, useMinWidth } from "@cntrl/ui";
import { useUnreadCount } from "../../app/hooks/useNotifications.js";
import { NotificationMenu } from "./NotificationMenu.js";
import styles from "./NotificationBell.module.scss";

export interface NotificationBellProps {
  onNavigate?: (route: string) => void;
  /**
   * May this bell open the inline panel when the viewport is desktop-wide? The mobile header says NO and
   * means it structurally: it is the mobile chrome, and a Capacitor wrapper on a tablet is wide enough to
   * pass the viewport check while still having nowhere sensible to anchor a desktop popover.
   */
  allowPanel?: boolean;
}

/**
 * The notifications bell — unread badge, and either the anchored panel or a jump to the full page.
 *
 * Extracted 2026-08-31 from three copies (artist / roster / mobile headers). Two of them had drifted from
 * the third in a way that mattered: **the unread count was announced to nobody.** The artist and roster
 * shells put `aria-label="Notifications"` on the button and then a second `aria-label` on the badge SPAN
 * inside it — but a child's label is not folded into a control that already names itself, so the count was
 * invisible to assistive tech (WCAG 4.1.2). `MobileHeader` had found and fixed this, and its fix is the one
 * kept here: the count lives in the BUTTON's accessible name, and the badge is decorative
 * (`aria-hidden`). AT hears the exact number even when the badge caps at "99+".
 *
 * Chrome is `IconButton` `outline` at the `md` rung (34px) with a 16px glyph — the house control, so the
 * bell matches its topbar neighbours by construction rather than by three copies of the same eight
 * declarations agreeing. The shells no longer pass a chrome class.
 *
 * The bell OWNS the panel's positioning context — the popup anchors to this button rather than to the
 * shell's actions row, or it right-aligns past whatever else lives there (the artist shell's band switcher).
 *
 * App-side rather than `@cntrl/ui` because it owns a data fetch (`useUnreadCount`) — the same boundary
 * `PersonAvatar` and `RecentReleaseCard` sit on. The ui package stays presentational.
 */
export function NotificationBell({ onNavigate, allowPanel = true }: NotificationBellProps) {
  const unread = useUnreadCount();
  const isDesktop = useMinWidth("md");
  const [open, setOpen] = useState(false);
  // The bell AND its panel are one dismiss boundary, so pressing the bell again closes the panel rather
  // than the outside-press handler closing it and the click re-opening it (ART-356).
  const wrapRef = useRef<HTMLDivElement>(null);

  const count = unread.data ?? 0;
  const usePanel = allowPanel && isDesktop;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <IconButton
        icon={Bell}
        // The count lives in the BUTTON's accessible name — see the note above; a badge-only label is
        // announced to nobody.
        label={count > 0 ? `Notifications, ${count} unread` : "Notifications"}
        tone="outline"
        iconSize={16}
        aria-haspopup={usePanel ? "dialog" : undefined}
        aria-expanded={usePanel ? open : undefined}
        onClick={() => (usePanel ? setOpen((v) => !v) : onNavigate?.("/notifications"))}
      />
      {count > 0 && (
        <span className={styles.badge} aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      )}
      {usePanel && open && (
        <NotificationMenu
          onClose={() => setOpen(false)}
          dismissBoundaryRef={wrapRef}
          onNavigate={onNavigate}
          onSeeAll={() => {
            setOpen(false);
            onNavigate?.("/notifications");
          }}
        />
      )}
    </div>
  );
}
